import { UploadOutlined } from "@ant-design/icons";
import { Image } from "antd";
import { FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Point, TaskAddPhoto, TaskRoute } from "tm-shared/types";

import { FALLBACK_IMAGE, ROUTES } from "@/constants";
import { GalleryUploader } from "@/domains/media";
import { PointInfo } from "@/domains/point";
import { SVScanButton } from "@/features/SupervisorScan";
import { makeCommand, useAsyncAction, useMakeCommand } from "@/hooks";
import { useAppDispatch, useTaskPayloadSelector } from "@/hooks/redux";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import BaseButton from "@/shared/ui/BaseButton";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import {
    clearTaskPayload,
    deleteAddPhotoTask,
    setAddPhotoTask,
} from "@/store/slices/progress";
import { setTaskCompleted } from "@/store/slices/work";
import { getFileUrl } from "@/utils/file/getFileUrl";

import { StyledAPImageWrapper, StyledGalleryWrapper } from "./Styled";

interface AddPhotoPageProps {
    point: Point;
    task: TaskAddPhoto;
}

export const AddPhotoPage: FC<AddPhotoPageProps> = ({ point, task }) => {
    const dispatch = useAppDispatch();

    const taskId = task.id;

    const currentPhotoId = useTaskPayloadSelector(TaskRoute.AddPhoto, taskId);

    const navigate = useNavigate();

    const pointId = point.id;

    const savedPhotos = currentPhotoId ? [currentPhotoId] : [];

    const { makeTaskCompleteCommands } = useMakeCommand();

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        if (!currentPhotoId) return;

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: {
                taskId,
                hasPhoto: false,
                payload: { result: true },
            },
        });

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: { pointId, taskId },
                commands: [
                    makeCommand({
                        type: "putPointChangePhoto",
                        title: "Збереження фото",
                        params: {
                            photoId: currentPhotoId,
                            buildingId: point.market.building.id,
                        },
                    }),

                    ...taskCompleteCommands,

                    makeCommand({
                        type: "deletePhotoFromStorage",
                        title: "Видалення фото зі сховища",
                        params: currentPhotoId,
                        options: {
                            stopOnError: false,
                        },
                    }),
                ],
            }),
        );

        dispatch(clearTaskPayload({ taskId }));
        dispatch(setTaskCompleted({ targetId: pointId, taskId }));
        navigate(ROUTES.WorkPlan, { replace: true });
    });

    const canSubmit = !!currentPhotoId;

    const handleDeletePhoto = useCallback(() => {
        dispatch(deleteAddPhotoTask({ taskId }));
    }, [taskId, dispatch]);

    const handleUploadPhoto = useCallback(
        (newPhotoId: string) => {
            dispatch(
                setAddPhotoTask({
                    taskId,
                    photoId: newPhotoId,
                }),
            );
        },
        [taskId, dispatch],
    );

    return (
        <>
            <Header
                title={task.name}
                back
                rightActions={
                    <SVScanButton pointId={pointId} taskId={task.id} />
                }
            />

            <MainContainer>
                <PointInfo point={point} />

                <StyledAPImageWrapper>
                    <Image
                        width="100%"
                        height="100%"
                        src={getFileUrl(point.market.building.photo)}
                        fallback={FALLBACK_IMAGE}
                    />
                </StyledAPImageWrapper>

                <StyledGalleryWrapper>
                    <GalleryUploader
                        pointId={pointId}
                        onDelete={handleDeletePhoto}
                        onUpload={handleUploadPhoto}
                        previewTitle={task.name}
                        savedPhotos={savedPhotos}
                        disabled={!!currentPhotoId}
                        galleryConfig={{ skipUpload: true }}
                    />
                </StyledGalleryWrapper>

                <BaseButton
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={handleComplete}
                    marginTop="16px"
                    disabled={!canSubmit}
                    loading={completeLoading}
                >
                    Завантажити фото
                </BaseButton>
            </MainContainer>

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};
